import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { PRODUCTS } from "@/lib/products";
import { inrFull } from "@/lib/format";
import { submitEnquiryCF7 } from "@/lib/wp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPLOYMENT = ["Salaried", "Self-employed", "Business owner", "Professional", "Other"];

const inputCls =
  "w-full rounded-xl border border-sand bg-white px-4 py-3 text-sm text-charcoal outline-none transition-colors placeholder:text-slate/50 focus:border-brass";

export const EnquiryForm = ({ prefill = {} }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    loan_type: prefill.loan_type || PRODUCTS[0].name,
    amount: prefill.amount || "",
    city: "",
    employment_type: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Enter your full name";
    const phone = form.phone.replace(/[\s-]/g, "").replace(/^(\+91|91)/, "");
    if (!/^[6-9]\d{9}$/.test(phone)) e.phone = "Enter a valid 10-digit mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address";
    if (!form.amount || Number(form.amount) <= 0) e.amount = "Enter the loan amount";
    if (!form.city.trim()) e.city = "Enter your city";
    if (!form.consent) e.consent = "Please accept to proceed";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const summary = [
      `Loan type: ${form.loan_type}`,
      `Amount: ${inrFull(Number(form.amount))}`,
      `City: ${form.city}`,
      form.employment_type && `Employment: ${form.employment_type}`,
      prefill.emi && `Calculated EMI: ${inrFull(Number(prefill.emi))}`,
      prefill.rate && `Rate used: ${prefill.rate}% p.a.`,
      prefill.tenure && `Tenure: ${prefill.tenure} months`,
    ].filter(Boolean).join("\n");

    const cf7Fields = {
      "your-name": form.name,
      "your-phone": form.phone,
      "your-email": form.email,
      "loan-type": form.loan_type,
      "loan-amount": String(form.amount),
      "your-city": form.city,
      "employment-type": form.employment_type,
      "calculated-emi": prefill.emi ? String(prefill.emi) : "",
      "your-message": summary,
    };
    const backendPayload = {
      name: form.name,
      phone: form.phone,
      email: form.email,
      loan_type: form.loan_type,
      amount: Number(form.amount),
      city: form.city,
      employment_type: form.employment_type,
      tenure_months: prefill.tenure ? Number(prefill.tenure) : null,
      interest_rate: prefill.rate ? Number(prefill.rate) : null,
      emi: prefill.emi ? Number(prefill.emi) : null,
      consent: true,
    };

    try {
      // The backend is the system of record (account tracking + applications inbox).
      // CF7, when configured on WordPress, also receives a best-effort copy.
      let cf7Delivered = false;
      try {
        await submitEnquiryCF7(cf7Fields);
        cf7Delivered = true;
      } catch { /* CF7 not configured yet */ }
      await axios.post(`${API}/enquiries`, { ...backendPayload, cf7_delivered: cf7Delivered }, { withCredentials: true });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="enquiry-success" className="rounded-3xl border border-sage/40 bg-white p-10 text-center shadow-[0_30px_80px_rgba(30,74,61,0.1)]">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-concordia/[0.08]">
          <CheckCircle2 className="h-8 w-8 text-concordia" />
        </span>
        <h3 className="mt-6 font-serif text-3xl font-semibold text-concordia">
          Thank you, {form.name.split(" ")[0]}.
        </h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate">
          Your {form.loan_type} enquiry for {inrFull(Number(form.amount))} has been received.
          A Loan Salaah advisor will call you on +91 {form.phone.replace(/[\s-]/g, "").replace(/^(\+91|91)/, "")},
          usually within one working day.
        </p>
        {user ? (
          <Link
            to="/account"
            data-testid="enquiry-track-link"
            className="mt-6 inline-block rounded-full border border-concordia/30 px-6 py-2.5 text-sm font-semibold text-concordia transition-colors hover:bg-concordia hover:text-ivory"
          >
            Track this application in your account →
          </Link>
        ) : (
          <p className="mx-auto mt-6 max-w-md text-xs leading-relaxed text-slate">
            Want live status updates?{" "}
            <Link to="/signup" data-testid="enquiry-track-link" className="font-semibold text-concordia underline decoration-brass/50 underline-offset-2">
              Create a free account
            </Link>{" "}
            with {form.email} — this enquiry links to it automatically.
          </p>
        )}
        <button
          data-testid="enquiry-another-button"
          onClick={() => {
            setSubmitted(false);
            setForm({ name: "", phone: "", email: "", loan_type: PRODUCTS[0].name, amount: "", city: "", employment_type: "", consent: false });
          }}
          className="mt-8 block w-full rounded-full border border-concordia/30 px-6 py-2.5 text-sm font-semibold text-concordia transition-colors hover:bg-concordia hover:text-ivory sm:mx-auto sm:w-auto"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="enquiry-form"
      onSubmit={submit}
      className="rounded-3xl border border-sand bg-white p-6 shadow-[0_30px_80px_rgba(30,74,61,0.1)] sm:p-10"
      noValidate
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="apply-form-name-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Full name</label>
          <input id="apply-form-name-input" data-testid="apply-form-name-input" className={inputCls} placeholder="e.g. Priya Sharma"
            value={form.name} onChange={(e) => set("name", e.target.value)} />
          {errors.name && <p className="mt-1 text-xs text-red-700" data-testid="apply-form-name-error">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="apply-form-phone-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Mobile number</label>
          <div className="flex items-center rounded-xl border border-sand bg-white focus-within:border-brass">
            <span className="pl-4 text-sm font-semibold text-slate">+91</span>
            <input id="apply-form-phone-input" data-testid="apply-form-phone-input" className="w-full rounded-xl bg-transparent px-2 py-3 text-sm outline-none placeholder:text-slate/50"
              placeholder="98765 43210" inputMode="numeric" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          {errors.phone && <p className="mt-1 text-xs text-red-700" data-testid="apply-form-phone-error">{errors.phone}</p>}
        </div>
        <div>
          <label htmlFor="apply-form-email-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Email</label>
          <input id="apply-form-email-input" data-testid="apply-form-email-input" type="email" className={inputCls} placeholder="you@example.com"
            value={form.email} onChange={(e) => set("email", e.target.value)} />
          {errors.email && <p className="mt-1 text-xs text-red-700" data-testid="apply-form-email-error">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="apply-form-loan-select" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Loan type</label>
          <select id="apply-form-loan-select" data-testid="apply-form-loan-select" className={inputCls}
            value={form.loan_type} onChange={(e) => set("loan_type", e.target.value)}>
            {PRODUCTS.map((p) => (
              <option key={p.slug} value={p.name}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="apply-form-amount-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Loan amount (₹)</label>
          <input id="apply-form-amount-input" data-testid="apply-form-amount-input" type="number" className={inputCls} placeholder="500000"
            value={form.amount} onChange={(e) => set("amount", e.target.value)} />
          {errors.amount && <p className="mt-1 text-xs text-red-700" data-testid="apply-form-amount-error">{errors.amount}</p>}
        </div>
        <div>
          <label htmlFor="apply-form-city-input" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">City</label>
          <input id="apply-form-city-input" data-testid="apply-form-city-input" className={inputCls} placeholder="e.g. Pune"
            value={form.city} onChange={(e) => set("city", e.target.value)} />
          {errors.city && <p className="mt-1 text-xs text-red-700" data-testid="apply-form-city-error">{errors.city}</p>}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="apply-form-employment-select" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate">Employment type</label>
          <select id="apply-form-employment-select" data-testid="apply-form-employment-select" className={inputCls}
            value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)}>
            <option value="">Select…</option>
            {EMPLOYMENT.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
      </div>

      <label className="mt-6 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-slate">
        <input
          type="checkbox"
          data-testid="apply-form-consent-checkbox"
          checked={form.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-sand accent-[#1E4A3D]"
        />
        <span>
          I agree to be contacted by Loan Salaah and its lending partners about my enquiry, and accept the{" "}
          <a href="/legal/terms-of-use" className="font-medium text-concordia underline decoration-brass/50 underline-offset-2">Terms of Use</a> and{" "}
          <a href="/legal/privacy-policy" className="font-medium text-concordia underline decoration-brass/50 underline-offset-2">Privacy Policy</a>.
        </span>
      </label>
      {errors.consent && <p className="mt-1 text-xs text-red-700" data-testid="apply-form-consent-error">{errors.consent}</p>}

      <button
        type="submit"
        data-testid="apply-form-submit-button"
        disabled={submitting}
        className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-concordia px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-concordia-deep hover:shadow-[0_14px_34px_rgba(30,74,61,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
        {submitting ? "Submitting…" : "Submit enquiry"}
      </button>
      <p className="mt-4 text-center text-[0.7rem] leading-relaxed text-slate/70">
        This is an enquiry, not a loan application. No hard credit check is made at this stage.
      </p>
    </form>
  );
};
